/**
 * cdn-domain-firewall-rules-tab.tsx — Firewall Rules tab for a CDN domain (act-as).
 *
 * Rule list: each rule has condition (field/operator/value) + action.
 * Full PUT replaces the rules array. Mutations gated through ActAsMutationConfirm.
 *
 * Endpoints: GET/PUT /api/v3/cdn/domains/:id/firewall-rules
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Pencil, Trash2, ShieldAlert } from 'lucide-react';
import { Section, ActionBar } from '@vnetwork/ui/layout';
import {
  Card, CardHeader, CardTitle, CardDescription, CardBody,
  Button, IconButton, Input, Switch, Skeleton, Badge,
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
  Dialog, DialogHeader, DialogBody, DialogFooter,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { EmptyState } from '@vnetwork/ui/data';
import { TabError } from '@/pages/customers/detail/tab-states';
import {
  useActAsCDNFirewallRules,
  actAsCDNSecurityKeys,
  type FirewallRule,
  type FirewallCondition,
} from '@/api/act-as-cdn-security';
import { actAsClient } from '@/api/act-as-client';
import { ActAsMutationConfirm } from '../act-as-mutation-confirm';
import { NumericInput } from '@/components/numeric-input';

const ACTIONS = ['allow', 'block', 'challenge', 'log'] as const;
const CONDITION_FIELDS = ['ip', 'country', 'asn', 'url_path', 'user_agent', 'hostname', 'request_method'] as const;
const OPERATORS = ['equals', 'not_equals', 'contains', 'not_contains', 'starts_with', 'ends_with', 'regex', 'in', 'not_in'] as const;

const ACTION_TONE: Record<string, 'danger' | 'warning' | 'info' | 'neutral'> = {
  block: 'danger', challenge: 'warning', log: 'info', allow: 'neutral',
};

function blankCondition(): FirewallCondition {
  return { field: 'ip', operator: 'equals', value: '' };
}

function blankRule(): FirewallRule {
  return { name: '', priority: 100, enabled: true, action: 'block', conditions: [blankCondition()] };
}

interface Props { domainId: string }

export function CdnDomainFirewallRulesTab({ domainId }: Props) {
  const { t } = useTranslation();
  const q = useActAsCDNFirewallRules(domainId);
  const [rules, setRules] = useState<FirewallRule[]>([]);
  const [snapshot, setSnapshot] = useState<FirewallRule[]>([]);
  const [editing, setEditing] = useState<FirewallRule | null>(null);
  const [editIdx, setEditIdx] = useState<number | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    const next = q.data?.rules ?? [];
    setRules(next);
    setSnapshot(next);
  }, [q.data]);

  const dirty = JSON.stringify(rules) !== JSON.stringify(snapshot);

  const openNew = () => { setEditing(blankRule()); setEditIdx(null); };
  const openEdit = (r: FirewallRule, i: number) => { setEditing({ ...r }); setEditIdx(i); };

  const saveEdit = () => {
    if (!editing) return;
    setRules((prev) => {
      if (editIdx === null) return [...prev, editing];
      return prev.map((r, i) => (i === editIdx ? editing : r));
    });
    setEditing(null);
    setEditIdx(null);
  };

  const deleteRule = (i: number) => setRules((prev) => prev.filter((_, idx) => idx !== i));

  const setCondition = (ci: number, k: keyof FirewallCondition, v: string) => {
    if (!editing) return;
    const conds = editing.conditions.map((c, i) => (i === ci ? { ...c, [k]: v } : c));
    setEditing({ ...editing, conditions: conds });
  };
  const addCondition = () =>
    setEditing((e) => e ? { ...e, conditions: [...e.conditions, blankCondition()] } : e);
  const removeCondition = (ci: number) =>
    setEditing((e) => e ? { ...e, conditions: e.conditions.filter((_, i) => i !== ci) } : e);

  if (q.isLoading) return <Skeleton style={{ height: 320 }} />;
  if (q.isError) {
    return (
      <TabError
        title={t('cdn_domain.firewall_tab.load_error', 'Failed to load firewall rules')}
        onRetry={() => q.refetch()}
      />
    );
  }

  return (
    <Section
      title={t('cdn_domain.firewall_tab.title', 'Firewall Rules')}
      description={t('cdn_domain.firewall_tab.description', 'IP/geo/ASN-based allow, block, or challenge rules.')}
    >
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <Card>
          <CardHeader
            actions={
              <Button variant="secondary" size="sm" startIcon={<Plus size={14} />} onClick={openNew}>
                {t('cdn_domain.firewall_tab.add_rule', 'Add rule')}
              </Button>
            }
          >
            <CardTitle>{t('cdn_domain.firewall_tab.section_rules', 'Rules')}</CardTitle>
            <CardDescription>
              {t('cdn_domain.firewall_tab.section_rules_desc', '{{n}} rule(s) — lower priority number evaluated first.', { n: rules.length })}
            </CardDescription>
          </CardHeader>
          <CardBody>
            {rules.length === 0 ? (
              <EmptyState
                size="sm"
                icon={<ShieldAlert size={20} />}
                title={t('cdn_domain.firewall_tab.no_rules', 'No firewall rules')}
                description={t('cdn_domain.firewall_tab.no_rules_desc', 'Add a rule to allow, block, or challenge requests.')}
              />
            ) : (
              <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
                {rules.map((rule, i) => (
                  <div
                    key={i}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--vn-space-3)',
                      padding: 'var(--vn-space-3)',
                      borderRadius: 'var(--vn-radius-md)',
                      background: 'var(--vn-bg-subtle)',
                      opacity: rule.enabled ? 1 : 0.55,
                    }}
                  >
                    <span style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)', minWidth: 36 }}>
                      #{rule.priority}
                    </span>
                    <span style={{ flex: 1, fontWeight: 500, fontSize: 'var(--vn-font-size-sm)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {rule.name || t('cdn_domain.firewall_tab.untitled_rule', 'Untitled rule')}
                    </span>
                    <Badge tone={ACTION_TONE[rule.action] ?? 'neutral'} appearance="subtle" size="sm">
                      {rule.action}
                    </Badge>
                    <span style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>
                      {rule.conditions.length} cond
                    </span>
                    <IconButton variant="ghost" size="sm" aria-label={t('cdn_domain.firewall_tab.edit_rule', 'Edit')} onClick={() => openEdit(rule, i)}>
                      <Pencil size={14} />
                    </IconButton>
                    <IconButton variant="ghost" size="sm" aria-label={t('cdn_domain.firewall_tab.delete_rule', 'Delete')} onClick={() => deleteRule(i)}>
                      <Trash2 size={14} />
                    </IconButton>
                  </div>
                ))}
              </div>
            )}
          </CardBody>
        </Card>

        <ActionBar
          end={
            <>
              <Button variant="secondary" disabled={!dirty} onClick={() => setRules(snapshot)}>
                {t('common.cancel', 'Cancel')}
              </Button>
              <Button disabled={!dirty} onClick={() => setConfirmOpen(true)}>
                {t('cdn_domain.firewall_tab.action_save', 'Save')}
              </Button>
            </>
          }
        />
      </div>

      {/* Rule editor dialog */}
      {editing && (
        <Dialog open onOpenChange={(o) => { if (!o) { setEditing(null); setEditIdx(null); } }}>
          <DialogHeader>
            {editIdx === null
              ? t('cdn_domain.firewall_tab.dialog_add_title', 'Add firewall rule')
              : t('cdn_domain.firewall_tab.dialog_edit_title', 'Edit firewall rule')}
          </DialogHeader>
          <DialogBody style={{ display: 'grid', gap: 'var(--vn-space-4)', maxHeight: '70vh', overflowY: 'auto' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 'var(--vn-space-3)' }}>
              <Field label={t('cdn_domain.firewall_tab.field_name', 'Name')}>
                {(p) => (
                  <Input {...p} value={editing.name} placeholder={t('cdn_domain.firewall_tab.name_placeholder', 'e.g. Block Bad IPs')}
                    onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
                )}
              </Field>
              <Field label={t('cdn_domain.firewall_tab.field_priority', 'Priority')}>
                {(p) => (
                  <NumericInput {...p} value={editing.priority} style={{ width: 90 }}
                    onChange={(e) => setEditing({ ...editing, priority: Number(e.target.value) })} />
                )}
              </Field>
              <Field label={t('cdn_domain.firewall_tab.field_enabled', 'Enabled')}>
                {() => (
                  <Switch checked={editing.enabled} onCheckedChange={(v) => setEditing({ ...editing, enabled: v })} />
                )}
              </Field>
            </div>

            <Field label={t('cdn_domain.firewall_tab.field_action', 'Action')}>
              {(p) => (
                <Select value={editing.action} onValueChange={(v) => setEditing({ ...editing, action: v as FirewallRule['action'] })}>
                  <SelectTrigger {...p}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ACTIONS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </Field>

            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--vn-space-2)' }}>
                <span style={{ fontWeight: 500, fontSize: 'var(--vn-font-size-sm)' }}>
                  {t('cdn_domain.firewall_tab.conditions_label', 'Conditions (AND)')}
                </span>
                <Button variant="ghost" size="sm" startIcon={<Plus size={13} />} onClick={addCondition}>
                  {t('cdn_domain.firewall_tab.add_condition', 'Add')}
                </Button>
              </div>
              {editing.conditions.map((c, ci) => (
                <div key={ci} style={{ display: 'flex', gap: 'var(--vn-space-2)', alignItems: 'center', marginBottom: 'var(--vn-space-2)' }}>
                  <Select value={c.field} onValueChange={(v) => setCondition(ci, 'field', v)}>
                    <SelectTrigger style={{ width: 140 }}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CONDITION_FIELDS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={c.operator} onValueChange={(v) => setCondition(ci, 'operator', v)}>
                    <SelectTrigger style={{ width: 130 }}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {OPERATORS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input value={c.value} placeholder="value" style={{ flex: 1 }}
                    onChange={(e) => setCondition(ci, 'value', e.target.value)} />
                  <IconButton variant="ghost" size="sm" aria-label="Remove" onClick={() => removeCondition(ci)}>
                    <Trash2 size={13} />
                  </IconButton>
                </div>
              ))}
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="secondary" onClick={() => { setEditing(null); setEditIdx(null); }}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button onClick={saveEdit}>
              {editIdx === null ? t('cdn_domain.firewall_tab.dialog_add_btn', 'Add rule') : t('cdn_domain.firewall_tab.dialog_save_btn', 'Save rule')}
            </Button>
          </DialogFooter>
        </Dialog>
      )}

      <ActAsMutationConfirm
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={t('cdn_domain.firewall_tab.confirm_save_title', 'Save firewall rules')}
        summary={t('cdn_domain.firewall_tab.save_summary', 'Update {{n}} firewall rule(s) for this domain.', { n: rules.length })}
        queryKeysToInvalidate={[actAsCDNSecurityKeys.firewallRules(domainId)]}
        mutationFn={() => actAsClient.put(`/cdn/domains/${domainId}/firewall-rules`, { rules })}
        onSuccess={() => setSnapshot(rules)}
      />
    </Section>
  );
}
